import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CompanyService } from 'src/core/services/company.service';
import { LoaderService } from 'src/core/services/loader.service';
import { SageService } from 'src/core/services/sage.service';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces } from 'src/form/components/data/provinces';
import { Address, AddressEditPayload } from 'src/interfaces/address';
import { Company, CompanyType, EditCompanyPayload } from 'src/interfaces/company';

@Component({
  selector: 'app-company-view',
  templateUrl: './company-view.component.html',
  styleUrls: ['./company-view.component.scss']
})
export class CompanyViewComponent implements OnInit {

  companyTypes: CompanyType[] = [];
  company: Company | null = null;
  selectedDistributor: Company | null = null;
  showDistributor: boolean = false;
  address: Address | null = null;


  filteredCountriesForIndividual: Observable<Country[]>;

  countries = all_countries;
  provinces = all_provinces;

  isLoading: boolean = false;

  companyDetailsForm = this._formBuilder.group({
    companyName: ['', Validators.required],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')])],
    phoneNumberAlt: ['', Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')],
    practiceNumber: ['', Validators.required],
    vatRegistrationNumber: ['', Validators.pattern(/[0-9]+/)],
    companyType: ['', Validators.required],
    distributor: ['']
  });

  addressDetailsForm = this._formBuilder.group({
    country: ['South Africa', Validators.required],
    province: ['', Validators.required],
    city: [''],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    buildingAptNumber: ['', Validators.required],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])]
  });

  editMode: boolean = false;
  addingToSage: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _companyService: CompanyService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _loaderService: LoaderService,
    private _sageService: SageService,
  ) { }

  ngOnInit(): void {
    this._route
      .data
      .subscribe(data => {
        this.company = data.company.company;
        this.companyTypes = data.company.companyTypes;
        if (this.company) this.updateView(this.company);
      })

    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.filteredCountriesForIndividual = this
      .addressDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );


    this.companyDetailsForm
      .controls['companyType']
      .valueChanges
      .subscribe(value => {
        // choose to show or not show distributor field
        const companyType: CompanyType = value;
        if (companyType) this.toggleDistributor(companyType);
      });

  }

  toggleDistributor(companyType: CompanyType) {

    if (companyType.code === 'DIS') {
      this.showDistributor = false;
      this.companyDetailsForm
        .patchValue({
          distributor: null
        })
    } else {
      this.showDistributor = true;
    }

  }

  updateView(company: Company): void {
    // get company type

    const companyType: CompanyType | undefined = this.companyTypes
      .find(type => type.id === company.company_type_id);

    if (companyType) this.toggleDistributor(companyType);

    this.address = company.addresses[0];

    if (this.address) {
      this.addressDetailsForm.patchValue({
        country: this.address.country,
        province: this.address.province,
        city: this.address.city,
        addressLine1: this.address.address_1,
        addressLine2: this.address.address_2,
        buildingAptNumber: this.address.building_apt_number,
        postcode: this.address.postcode
      })
    }

    this.companyDetailsForm.patchValue({
      companyName: company.name,
      email: company.email,
      emailAlt: company.email_alt,
      phoneNumber: company.phone_number,
      phoneNumberAlt: company.phone_number_alt,
      practiceNumber: company.practice_number,
      vatRegistrationNumber: company.vat_registration_number,
      companyType: companyType ? companyType : null
    })

    this.selectedDistributor = company.distributor;
  }

  async save() {

    if (!this.company || !this.company.id) return;

    const addressPayload: AddressEditPayload = {
      building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
      address_1: this.addressDetailsForm.value.addressLine1,
      address_2: this.addressDetailsForm.value.addressLine2,
      province: this.addressDetailsForm.value.province,
      city: this.addressDetailsForm.value.city,
      postcode: this.addressDetailsForm.value.postcode,
      country: this.addressDetailsForm.value.country,
      id: this.address ? this.address.id : undefined,
    }

    const payload: EditCompanyPayload = {
      id: undefined,
      name: this.companyDetailsForm.value.companyName,
      email: this.companyDetailsForm.value.email,
      email_alt: this.companyDetailsForm.value.emailAlt,
      phone_number: this.companyDetailsForm.value.phoneNumber,
      phone_number_alt: this.companyDetailsForm.value.phoneNumberAlt,
      practice_number: this.companyDetailsForm.value.practiceNumber,
      vat_registration_number: this.companyDetailsForm.value.vatRegistrationNumber,
      company_type_id: this.companyDetailsForm.value.companyType.id,
      primary_contact_id: this.company.primary_contact_id,
      address: addressPayload,
      distributor_id: this.selectedDistributor ? this.selectedDistributor.id : undefined
    }

    const success: boolean = await this._companyService
      .editCompany(this.company.id, payload);

    if (success) {
      // get company latest data
      this.company = await this._companyService.getCompany(this.company.id);
      if (this.company) this.updateView(this.company);
    }
  }


  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  async openSearchDistributorDialog() {
    this.selectedDistributor = await this._companyService
      .openCompanySearchDialog("Search for a distributor", "", "DIS");

    if (!this.selectedDistributor) return;

    this
      .companyDetailsForm
      .patchValue({
        distributor: this.selectedDistributor
      });

  }

  deselectDistributor() {

    this.selectedDistributor = null;

    this
      .companyDetailsForm
      .patchValue({
        distributor: null
      });
  }

  /** Whether this company is linked to a Sage contact (show "On Sage"). */
  get isOnSage(): boolean {
    return !!(this.company?.sage_contact_id);
  }

  /** Add this company as a Sage contact (POST /sage/contacts). Refreshes company; keeps sage id if GET omits it. */
  async addToSage(): Promise<void> {
    if (!this.company || this.addingToSage) return;
    this.addingToSage = true;
    let createdSageId: string | undefined;
    try {
      const result = await this._sageService.createContactForCompany(this.company);
      if (result?.success) {
        createdSageId = result.sage_contact_id;
        if (createdSageId) {
          this.company = { ...this.company!, sage_contact_id: createdSageId };
        }
      }
      const updated = await this._companyService.getCompany(this.company!.id!);
      if (updated) {
        const merged =
          updated.sage_contact_id?.trim() ||
          createdSageId ||
          this.company?.sage_contact_id;
        this.company =
          merged != null && String(merged).trim()
            ? { ...updated, sage_contact_id: String(merged) }
            : updated;
      } else if (createdSageId) {
        this.company = { ...this.company!, sage_contact_id: createdSageId };
      }
    } finally {
      this.addingToSage = false;
    }
  }

  // Navigate back to company list preserving filters
  backToList() {
    const qp = this._route.snapshot.queryParamMap;
    const search = qp.get('search') || undefined;
    const companyType = qp.get('companyType') || undefined;
    this._router.navigate(['/portal/companies'], {
      queryParams: { search, companyType }
    });
  }

}
